import JSZip from 'jszip';
const FORM_JSON_RENDER_KEY_PREFIX = '_render:';
/** Archived-PDF grey vs green is role-keyed: only exact `participant` (any case) is green. */
export function isNonParticipantSignerType(signerType) {
    return typeof signerType === 'string' && signerType.toLowerCase() !== 'participant';
}
/**
 * Parses form.json to extract the pdfContentUrl value and signatureFields array.
 * The structure is: `{ "_render:<template>": { pdfContentUrl: { _value: "..." }, signatureFields: [...] } }`
 */
function parseFormJson(formJsonContent) {
    const parsed = JSON.parse(formJsonContent);
    const renderKey = Object.keys(parsed).find((k) => k.startsWith(FORM_JSON_RENDER_KEY_PREFIX));
    if (!renderKey) {
        throw new Error('Consent bundle form.json missing _render key');
    }
    const renderData = parsed[renderKey];
    const pdfContentUrl = renderData?.pdfContentUrl;
    const pdfRelativeUrl = typeof pdfContentUrl === 'string' ? pdfContentUrl : (pdfContentUrl?._value ?? '');
    if (!pdfRelativeUrl) {
        throw new Error('Consent bundle form.json missing pdfContentUrl');
    }
    const signatureFields = Array.isArray(renderData?.signatureFields)
        ? renderData.signatureFields
        : [];
    const dateFields = Array.isArray(renderData?.dateFields) ? renderData.dateFields : [];
    const textFields = Array.isArray(renderData?.textFields) ? renderData.textFields : [];
    const questions = Array.isArray(renderData?.questions) ? renderData.questions : [];
    return { pdfRelativeUrl, signatureFields, dateFields, textFields, questions };
}
function firstZipFile(zip, exact, fallback) {
    return zip.file(exact) ?? zip.file(fallback)[0] ?? null;
}
/** Same lookup PTS blank-PDF download uses: root `icf.pdf`, then `assets/*.pdf`, then any PDF. */
function findBundlePdfFile(zip) {
    return zip.file('icf.pdf') ?? zip.file(/assets\/.*\.pdf$/i)[0] ?? zip.file(/\.pdf$/i)[0] ?? null;
}
/**
 * Pull a JSON array out of compiled `bundle.js`. The published runtime zip does
 * not include `form.json`; geometry is inlined as `"signatureFields":[...]`
 * (and `"dateFields":[...]` / `"textFields":[...]`) on the consent app bootstrap. Earlier occurrences
 * such as `signatureFields = []` are skipped by `isPayload`.
 */
function extractBundleJsonArray(bundleJs, marker, isPayload) {
    let searchFrom = 0;
    while (searchFrom < bundleJs.length) {
        const idx = bundleJs.indexOf(marker, searchFrom);
        if (idx === -1) {
            return [];
        }
        searchFrom = idx + marker.length;
        let start = -1;
        for (let i = idx + marker.length; i < Math.min(bundleJs.length, idx + 2000); i++) {
            if (bundleJs[i] === '[') {
                start = i;
                break;
            }
        }
        if (start === -1) {
            continue;
        }
        let depth = 0;
        let end = -1;
        let inString = false;
        let escaped = false;
        for (let i = start; i < bundleJs.length; i++) {
            const ch = bundleJs[i];
            if (escaped) {
                escaped = false;
                continue;
            }
            if (ch === '\\') {
                escaped = true;
                continue;
            }
            if (ch === '"') {
                inString = !inString;
                continue;
            }
            if (inString) {
                continue;
            }
            if (ch === '[') {
                depth++;
            }
            else if (ch === ']') {
                depth--;
                if (depth === 0) {
                    end = i + 1;
                    break;
                }
            }
        }
        if (end === -1) {
            continue;
        }
        try {
            const fields = JSON.parse(bundleJs.slice(start, end));
            if (Array.isArray(fields) && isPayload(fields)) {
                return fields;
            }
        }
        catch {
            // Keep scanning; default-arg `[]` and other non-payload hits are ignored.
        }
    }
    return [];
}
function extractSignatureFieldsFromBundle(bundleJs) {
    return extractBundleJsonArray(bundleJs, 'signatureFields', (fields) => fields.some((field) => field && typeof field === 'object' && 'xPct' in field && 'yPct' in field && !('optionKey' in field)));
}
function extractGeometryFieldsFromBundle(bundleJs, marker) {
    return extractBundleJsonArray(bundleJs, marker, (fields) => fields.some((field) => field && typeof field === 'object' && 'ec__key' in field && 'xPct' in field && 'yPct' in field));
}
/**
 * Questions hold their geometry one level down, on each option, so the payload test looks for a
 * `choices` array rather than `xPct` — matching `signatureFields`, which excludes anything carrying
 * `optionKey`.
 */
function extractQuestionsFromBundle(bundleJs) {
    return extractBundleJsonArray(bundleJs, 'questions', (fields) => fields.some((field) => field &&
        typeof field === 'object' &&
        'ec__key' in field &&
        Array.isArray(field.choices)));
}
async function resolveBundlePdfAndFields(zip) {
    const formJsonFile = zip.file('form.json');
    if (formJsonFile) {
        const { pdfRelativeUrl, signatureFields, dateFields, textFields, questions } = parseFormJson(await formJsonFile.async('string'));
        const pdfPath = pdfRelativeUrl.replace(/\?.*$/, '').replace(/^\.\//, '');
        const pdfFile = zip.file(pdfPath);
        if (!pdfFile) {
            throw new Error(`Consent bundle does not contain PDF file: ${pdfPath}`);
        }
        return { pdfFile, signatureFields, dateFields, textFields, questions };
    }
    const pdfFile = findBundlePdfFile(zip);
    if (!pdfFile) {
        throw new Error('Consent bundle does not contain PDF file');
    }
    const bundleJsFile = firstZipFile(zip, 'bundle.js', /bundle\.js$/);
    const bundleJs = bundleJsFile ? await bundleJsFile.async('string') : '';
    return {
        pdfFile,
        signatureFields: bundleJs ? extractSignatureFieldsFromBundle(bundleJs) : [],
        dateFields: bundleJs ? extractGeometryFieldsFromBundle(bundleJs, 'dateFields') : [],
        textFields: bundleJs ? extractGeometryFieldsFromBundle(bundleJs, 'textFields') : [],
        questions: bundleJs ? extractQuestionsFromBundle(bundleJs) : [],
    };
}
/**
 * Extracts the original PDF bytes and signature field geometry from a consent
 * bundle zip, then merges the field positions with the collected signature data
 * to produce overlay fields ready for PDF generation.
 */
export async function extractBundlePdfData(bundleUrl, signedSignatures, enteredDates = [], enteredTexts = [], enteredChoices = []) {
    const response = await fetch(bundleUrl);
    if (!response.ok) {
        throw new Error(`Failed to fetch consent bundle: ${response.status}`);
    }
    const buffer = await response.arrayBuffer();
    const zip = await JSZip.loadAsync(new Uint8Array(buffer));
    const { pdfFile, signatureFields, dateFields, textFields, questions } = await resolveBundlePdfAndFields(zip);
    const pdfBytes = await pdfFile.async('uint8array');
    const signedByKey = new Map();
    for (const summary of signedSignatures) {
        signedByKey.set(summary.ec__key, summary);
    }
    const signedOverlays = signatureFields
        .filter((field) => field.ec__key && signedByKey.has(field.ec__key))
        .map((field) => {
        const summary = signedByKey.get(field.ec__key);
        return {
            ec__key: field.ec__key,
            name: summary.name,
            id: summary.id,
            date: summary.date,
            meaning: summary.meaning,
            attestation: summary.attestation,
            clockSource: summary.clockSource,
            deviceIanaZone: summary.deviceIanaZone,
            deviceUtcOffset: summary.deviceUtcOffset,
            deviceLocalTime: summary.deviceLocalTime,
            page: field.page,
            xPct: field.xPct,
            yPct: field.yPct,
            widthPct: field.widthPct,
            heightPct: field.heightPct,
            type: field.type,
            otherSigner: isNonParticipantSignerType(field.signerType),
        };
    });
    // Only answered fields are stamped. An empty date field leaves no mark on the archived record,
    // the same way an unsigned signature field does not print an empty stamp.
    const valueByFieldKey = new Map(enteredDates.filter((entry) => entry.value).map((e) => [e.fieldKey, e.value]));
    const dateOverlays = dateFields
        .filter((field) => field.ec__key && valueByFieldKey.has(field.ec__key))
        .map((field) => ({
        ec__key: field.ec__key,
        value: valueByFieldKey.get(field.ec__key),
        page: field.page,
        xPct: field.xPct,
        yPct: field.yPct,
        widthPct: field.widthPct,
        heightPct: field.heightPct,
    }));
    const textByFieldKey = new Map(enteredTexts.filter((entry) => entry.value !== '').map((e) => [e.fieldKey, e.value]));
    const textOverlays = textFields
        .filter((field) => field.ec__key && textByFieldKey.has(field.ec__key))
        .map((field) => ({
        ec__key: field.ec__key,
        value: textByFieldKey.get(field.ec__key),
        page: field.page,
        xPct: field.xPct,
        yPct: field.yPct,
        widthPct: field.widthPct,
        heightPct: field.heightPct,
        otherSigner: isNonParticipantSignerType(field.signerType),
    }));
    // One overlay per selected option: a checkbox question marks every option the signer chose, and
    // each option carries its own page and box, so a question can span pages.
    const selectedByQuestion = new Map(enteredChoices
        .filter((entry) => entry.questionKey && Array.isArray(entry.selectedOptionKeys))
        .map((entry) => [entry.questionKey, new Set(entry.selectedOptionKeys)]));
    const choiceOverlays = questions.flatMap((question) => {
        const selected = question.ec__key ? selectedByQuestion.get(question.ec__key) : undefined;
        if (!selected?.size || !Array.isArray(question.choices)) {
            return [];
        }
        return question.choices
            .filter((option) => selected.has(option.optionKey))
            .map((option) => ({
            ec__key: question.ec__key,
            optionKey: option.optionKey,
            multi: question.isMultiSelect === true,
            page: option.page,
            xPct: option.xPct,
            yPct: option.yPct,
            widthPct: option.widthPct,
            heightPct: option.heightPct,
        }));
    });
    return { pdfBytes, signedOverlays, dateOverlays, textOverlays, choiceOverlays };
}
